'use client';

import { type Component, type ComponentProps } from '../../types/component';
import { tv } from '../../utils/tv';
import { useListContext, useListItemButtonContext } from './ListContext';

const listItemAffixClasses = { root: 'zds-list-item-affix-root' } as const;

const listItemAffixStyles = tv({
  base: [listItemAffixClasses.root, 'flex shrink-0'],
  variants: {
    alignSelf: {
      top: 'self-start',
      center: 'self-center',
    },
    color: {
      neutral: '',
      'static-white': 'text-static-white-main',
      'accent-primary': '',
    },
    isSelected: {
      true: '',
    },
    isDisabled: {
      // Opacity + grayscale approximate a disabled tone for any affix content
      // (icons, Flag, Avatar images/initials) that may not inherit currentColor.
      true: 'opacity-disabled [&>*]:grayscale',
    },
  },
  compoundVariants: [
    {
      color: 'accent-primary',
      isSelected: true,
      class: 'text-accent-primary-main',
    },
  ],
});

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ListItemAffixBaseProps {}

type ListItemAffixProps = ComponentProps<ListItemAffixBaseProps, 'span'>;

type ListItemAffixComponent = Component<ListItemAffixBaseProps, 'span'>;

const ListItemAffix: ListItemAffixComponent = (props) => {
  const { children, className, ref, ...rest } = props;

  const context = useListContext();
  const buttonContext = useListItemButtonContext();
  const alignSelf = context?.alignItems ?? 'center';
  const isDisabled = buttonContext?.isDisabled ?? false;
  const isSelected = buttonContext?.isSelected ?? false;
  const color = isDisabled ? undefined : (context?.color ?? 'neutral');
  return (
    <span
      {...rest}
      ref={ref}
      className={listItemAffixStyles({
        alignSelf,
        color,
        isSelected,
        isDisabled,
        className,
      })}
    >
      {children}
    </span>
  );
};

ListItemAffix.displayName = 'List.ItemAffix';

export {
  ListItemAffix,
  type ListItemAffixProps,
  type ListItemAffixBaseProps,
  listItemAffixClasses,
};
